import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import SvgLink from '../components/SvgLink';
import { projectsData } from '../data/projects-meta';
import {
    isFirebaseReady,
    subscribeSponsorships,
    recordSponsorship,
} from '../lib/sponsorship';
import { startCheckout } from '../lib/stripe-checkout';
import { SPONSOR_AMOUNT_TWD } from '../data/sponsor-constants';

// Helper function to check if URL is internal route
const isInternalRoute = (url) => {
    return url && (url.startsWith('/') || url.startsWith(window.location.origin));
};

const ProjectsContainer = styled.div`
    min-height: 100vh;
    padding: 100px 20px 60px 20px;
    background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
    color: white;
`;

const ContentWrapper = styled.div`
    max-width: 1000px;
    margin: 0 auto;
`;

const Header = styled.div`
    text-align: center;
    margin-bottom: 50px;
`;

const Title = styled.h1`
    font-size: 3rem;
    margin-bottom: 15px;
    font-weight: 700;

    @media (max-width: 768px) {
        font-size: 2rem;
    }
`;

const Subtitle = styled.p`
    font-size: 1.3rem;
    opacity: 0.9;
    margin-bottom: 10px;

    @media (max-width: 768px) {
        font-size: 1.1rem;
    }
`;

const Description = styled.p`
    font-size: 1rem;
    opacity: 0.8;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
`;

const ProjectCard = styled.div`
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    padding: 30px;
    margin-bottom: 30px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    transition: transform 0.3s ease;
    position: relative;

    &:hover {
        transform: translateY(-5px);
    }

    @media (max-width: 768px) {
        padding: 20px;
    }
`;

const ProjectThumbnail = styled.a`
    width: 100%;
    max-width: 600px;
    margin: 0 auto 25px auto;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
    transition: transform 0.3s ease;
    display: block;
    position: relative;
    cursor: pointer;
    text-decoration: none;

    &:hover {
        transform: scale(1.02);
    }

    &:hover .thumbnail-overlay {
        opacity: 1;
    }

    img {
        width: 100%;
        height: auto;
        display: block;
        object-fit: cover;
        transition: filter 0.3s ease;
    }

    &:hover img {
        filter: brightness(0.8);
    }
`;

const ThumbnailOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(155, 89, 182, 0.85) 0%, rgba(142, 68, 173, 0.85) 100%);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: white;
    font-size: 1.2rem;
    font-weight: 600;
    opacity: 0;
    transition: opacity 0.3s ease;
    border-radius: 12px;
    box-shadow: inset 0 0 20px rgba(0, 0, 0, 0.2);

    @media (max-width: 768px) {
        font-size: 1rem;
    }
`;

const ThumbnailOverlayIcon = styled.svg`
    width: 24px;
    height: 24px;
    fill: currentColor;
`;

const ProjectHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${props => props.expanded ? '20px' : '0'};
    flex-wrap: wrap;
    gap: 15px;
    position: relative;
    cursor: pointer;
    padding: ${props => props.expanded ? '0' : '10px 0'};
    transition: all 0.3s ease;
`;

const ProjectHeaderCollapsed = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    gap: 15px;
    flex-wrap: wrap;

    @media (max-width: 480px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }
`;

const ProjectHeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 0;
    flex-wrap: wrap;

    @media (max-width: 480px) {
        width: 100%;
        gap: 8px;
    }
`;

const ProjectHeaderRight = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
    flex-shrink: 0;

    @media (max-width: 480px) {
        width: 100%;
        justify-content: flex-end;
    }
`;

const ExpandButton = styled.button`
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.7);
    font-size: 1.2rem;
    cursor: pointer;
    padding: 5px 10px;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;

    &:hover {
        color: rgba(255, 255, 255, 1);
        background: rgba(255, 255, 255, 0.1);
    }
`;

const ProjectContent = styled.div`
    overflow: hidden;
    transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s ease, margin-top 0.4s ease, padding-bottom 0.4s ease;
    max-height: ${props => props.expanded ? '5000px' : '0'};
    opacity: ${props => props.expanded ? '1' : '0'};
    margin-top: ${props => props.expanded ? '20px' : '0'};
    padding-bottom: ${props => props.expanded ? '80px' : '0'};
    pointer-events: ${props => props.expanded ? 'auto' : 'none'};
`;

const VotingSectionWrapper = styled.div`
    position: ${props => props.expanded ? 'absolute' : 'static'};
    bottom: ${props => props.expanded ? '30px' : 'auto'};
    right: ${props => props.expanded ? '30px' : 'auto'};
    margin-top: ${props => props.expanded ? '0' : '0'};
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 10;
    opacity: ${props => props.expanded ? '1' : '1'};

    @media (max-width: 768px) {
        bottom: ${props => props.expanded ? '20px' : 'auto'};
        right: ${props => props.expanded ? '20px' : 'auto'};
    }
`;

const VotingSection = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    position: relative;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;

const ProjectTitle = styled.h2`
    font-size: 1.8rem;
    margin: 0;
    display: flex;
    align-items: center;
    word-break: break-word;
    gap: 10px;
    flex: 1;

    @media (max-width: 480px) {
        flex: none;
        width: 100%;
        font-size: 1.3rem;
    }

    @media (max-width: 768px) {
        font-size: 1.4rem;
    }
`;

const StatusBadge = styled.span`
    padding: 6px 16px;
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: ${props => {
        switch (props.status) {
            case 'done':
                return '#27ae60';
            case 'wip':
                return '#f39c12';
            case 'pending':
                return '#3498db';
            case 'paused':
                return '#e67e22';
            case 'not yet':
                return '#95a5a6';
            default:
                return '#7f8c8d';
        }
    }};
    color: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;

const PinnedBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border-radius: 16px;
    font-size: 0.85rem;
    font-weight: 700;
    background: linear-gradient(135deg, #f1c40f 0%, #f39c12 100%);
    color: #1b263b;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;

const ProjectDescription = styled.p`
    font-size: 1.1rem;
    line-height: 1.8;
    opacity: 0.9;
    margin-bottom: 25px;
`;

const TodosSection = styled.div`
    margin-top: 20px;
    margin-bottom: 25px;
    padding: 15px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 10px;
    border-left: 3px solid #3498db;
`;

const TodosTitle = styled.h4`
    font-size: 1rem;
    margin-bottom: 12px;
    opacity: 0.9;
    font-weight: 600;
    color: #3498db;
`;

const TodosList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
`;

const TodoItem = styled.li`
    font-size: 0.9rem;
    line-height: 1.6;
    opacity: 0.85;
    margin-bottom: 8px;
    padding-left: 20px;
    position: relative;

    &:last-child {
        margin-bottom: 0;
    }

    &::before {
        content: '▸';
        position: absolute;
        left: 0;
        color: ${props => props.completed ? '#27ae60' : '#f39c12'};
    }

    ${props => props.completed && `
        text-decoration: line-through;
        opacity: 0.6;
    `}
`;

const SubProjectsSection = styled.div`
    margin-top: 20px;
    margin-bottom: 25px;
    padding: 15px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 10px;
    border-left: 3px solid #9b59b6;
    position: relative;

    &::before {
        content: '';
        position: absolute;
        top: 15px;
        left: -3px;
        width: 3px;
        height: 20px;
        background: linear-gradient(180deg, #9b59b6 0%, transparent 100%);
        border-radius: 0 0 0 3px;
    }
`;

const SubProjectsIndicator = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 15px;
    gap: 8px;
    
    &::before {
        content: '';
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background: #9b59b6;
        box-shadow: 0 0 8px rgba(155, 89, 182, 0.5);
    }
    
    &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: linear-gradient(90deg, rgba(155, 89, 182, 0.3) 0%, transparent 100%);
    }
`;

const SubProjectItem = styled.div`
    margin-bottom: 15px;
    padding: 12px;
    background: rgba(255, 255, 255, 0.03);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.08);
        transform: translateX(5px);
    }

    &:last-child {
        margin-bottom: 0;
    }
`;

const SubProjectName = styled.div`
    font-size: 0.95rem;
    font-weight: 600;
    margin-bottom: 5px;
    color: #9b59b6;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const SubProjectDescription = styled.div`
    font-size: 0.85rem;
    opacity: 0.8;
    margin-bottom: 8px;
    margin-left: 20px;
`;

const SubProjectStatus = styled.span`
    font-size: 0.75rem;
    padding: 3px 8px;
    border-radius: 12px;
    background: ${props => {
        switch (props.status) {
            case 'done':
                return '#27ae60';
            case 'wip':
                return '#f39c12';
            case 'pending':
                return '#3498db';
            case 'paused':
                return '#e67e22';
            default:
                return '#95a5a6';
        }
    }};
    color: white;
    margin-left: 20px;
`;

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    backdrop-filter: blur(5px);
    z-index: 2000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    animation: fadeIn 0.3s ease;

    @keyframes fadeIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }
`;

const ModalContent = styled.div`
    background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
    border-radius: 20px;
    padding: 30px;
    max-width: 800px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
    animation: slideUp 0.3s ease;
    position: relative;

    @keyframes slideUp {
        from {
            transform: translateY(50px);
            opacity: 0;
        }
        to {
            transform: translateY(0);
            opacity: 1;
        }
    }

    @media (max-width: 768px) {
        padding: 20px;
        max-height: 95vh;
    }
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 25px;
    padding-bottom: 20px;
    border-bottom: 2px solid rgba(255, 255, 255, 0.2);
`;

const ModalTitle = styled.h2`
    font-size: 2rem;
    margin: 0;
    color: #9b59b6;
    display: flex;
    align-items: center;
    gap: 15px;
    flex: 1;
    flex-wrap: wrap;

    @media (max-width: 768px) {
        font-size: 1.5rem;
    }
`;

const ParentProjectLabel = styled.span`
    font-size: 0.7rem;
    opacity: 0.6;
    font-weight: 400;
    font-style: italic;
    margin-left: 8px;
`;

const CloseButton = styled.button`
    background: rgba(255, 255, 255, 0.1);
    border: none;
    color: white;
    font-size: 1.5rem;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.2);
        transform: rotate(90deg);
    }
`;

const ModalDescription = styled.p`
    font-size: 1.1rem;
    line-height: 1.8;
    opacity: 0.9;
    margin-bottom: 30px;
`;

const ChangelogSection = styled.div`
    margin-top: 30px;
`;

const ChangelogTitle = styled.h3`
    font-size: 1.5rem;
    margin-bottom: 20px;
    color: #3498db;
    display: flex;
    align-items: center;
    gap: 10px;
`;

const ChangelogList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
`;

const ChangelogItem = styled.div`
    padding: 15px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 10px;
    border-left: 4px solid ${props => props.completed ? '#27ae60' : '#f39c12'};
    display: flex;
    align-items: flex-start;
    gap: 12px;
`;

const ChangelogIcon = styled.span`
    font-size: 1.2rem;
    flex-shrink: 0;
`;

const ChangelogText = styled.span`
    flex: 1;
    font-size: 1rem;
    line-height: 1.6;
    opacity: ${props => props.completed ? 0.7 : 0.9};
    text-decoration: ${props => props.completed ? 'line-through' : 'none'};
`;

const ViewButton = styled(Link)`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    background: linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%);
    color: white;
    text-decoration: none;
    border-radius: 12px;
    font-weight: 600;
    font-size: 1rem;
    margin-top: 20px;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(155, 89, 182, 0.3);

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(155, 89, 182, 0.4);
        background: linear-gradient(135deg, #8e44ad 0%, #9b59b6 100%);
    }

    &:active {
        transform: translateY(0);
    }
`;

const TotalVotes = styled.div`
    font-size: 0.95rem;
    opacity: 0.8;
    white-space: nowrap;
`;

const LoadingText = styled.p`
    text-align: center;
    opacity: 0.7;
    font-style: italic;
    margin: 40px 0;
`;

const ErrorText = styled.p`
    text-align: center;
    color: #e74c3c;
    opacity: 0.9;
    margin: 40px 0;
`;

const InfoText = styled.p`
    text-align: center;
    opacity: 0.7;
    font-size: 0.9rem;
    margin-top: 30px;
    font-style: italic;
`;

const SupportButton = styled.button`
    padding: 10px 20px;
    background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(52, 152, 219, 0.3);
    white-space: nowrap;

    @media (max-width: 480px) {
        padding: 8px 14px;
        font-size: 0.8rem;
    }

    &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(52, 152, 219, 0.4);
    }

    &:active:not(:disabled) {
        transform: translateY(0);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const DrinkModal = styled.div`
    position: absolute;
    bottom: 100%;
    right: 0;
    margin-bottom: 10px;
    background: rgba(44, 62, 80, 0.95);
    backdrop-filter: blur(10px);
    border-radius: 12px;
    padding: 10px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
    border: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 150px;
    animation: fadeInDown 0.3s ease;
    z-index: 10;

    @keyframes fadeInDown {
        from {
            opacity: 0;
            transform: translateY(-10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
`;

const DrinkOption = styled.button`
    padding: 10px 16px;
    background: ${props => props.voteType === 'coffee' ? 'rgba(139, 69, 19, 0.3)' : 'rgba(255, 107, 157, 0.3)'};
    border: 1px solid ${props => props.voteType === 'coffee' ? 'rgba(139, 69, 19, 0.5)' : 'rgba(255, 107, 157, 0.5)'};
    color: white;
    border-radius: 8px;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    text-align: left;

    &:hover {
        background: ${props => props.voteType === 'coffee' ? 'rgba(139, 69, 19, 0.5)' : 'rgba(255, 107, 157, 0.5)'};
        transform: translateX(5px);
    }

    &:active {
        transform: translateX(3px);
    }
`;

const StatsPanel = styled.div`
    background: rgba(255, 255, 255, 0.07);
    border-radius: 16px;
    padding: 20px 24px;
    margin-bottom: 30px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(6px);
`;

const StatsPanelHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0;
    cursor: pointer;
    user-select: none;
`;

const StatsPanelTitle = styled.h3`
    font-size: 1rem;
    font-weight: 600;
    margin: 0;
    opacity: 0.85;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const StatsPanelToggle = styled.span`
    font-size: 0.85rem;
    opacity: 0.6;
`;

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
    gap: 12px;
    margin-top: 16px;
`;

const StatCard = styled.div`
    background: rgba(255, 255, 255, 0.05);
    border-radius: 10px;
    padding: 14px 16px;
    text-align: center;
`;

const StatValue = styled.div`
    font-size: 1.6rem;
    font-weight: 700;
    color: ${props => props.color || 'white'};
    line-height: 1.1;
`;

const StatLabel = styled.div`
    font-size: 0.75rem;
    opacity: 0.65;
    margin-top: 4px;
    line-height: 1.3;
`;

const StatsProjectTable = styled.div`
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const StatsProjectRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    background: rgba(255, 255, 255, 0.04);
    border-radius: 8px;
    font-size: 0.85rem;
    gap: 10px;
    flex-wrap: wrap;
`;

const StatsProjectName = styled.span`
    opacity: 0.85;
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const StatsProjectNumbers = styled.span`
    display: flex;
    gap: 12px;
    flex-shrink: 0;
    opacity: 0.8;
    font-size: 0.8rem;
`;

const StatsProjectAmount = styled.span`
    color: #2ecc71;
    font-weight: 600;
`;

const SortBar = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;
    font-size: 0.9rem;

    @media (max-width: 480px) {
        justify-content: flex-start;
    }
`;

const SortLabel = styled.span`
    opacity: 0.75;
`;

const SortSelect = styled.select`
    padding: 8px 14px;
    background: rgba(255, 255, 255, 0.08);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    font-size: 0.9rem;
    cursor: pointer;
    transition: all 0.2s ease;
    appearance: none;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='white'><path d='M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z'/></svg>");
    background-repeat: no-repeat;
    background-position: right 10px center;
    background-size: 16px;
    padding-right: 34px;

    &:hover {
        background-color: rgba(255, 255, 255, 0.14);
    }

    option {
        background: #2c3e50;
        color: white;
    }
`;

const Toast = styled.div`
    position: fixed;
    bottom: 30px;
    right: 30px;
    background: rgba(44, 62, 80, 0.95);
    backdrop-filter: blur(10px);
    color: white;
    padding: 15px 24px;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
    z-index: 3000;
    animation: slideInUp 0.3s ease;

    @keyframes slideInUp {
        from {
            transform: translateY(100px);
            opacity: 0;
        }
        to {
            transform: translateY(0);
            opacity: 1;
        }
    }

    @media (max-width: 768px) {
        bottom: 20px;
        right: 20px;
        left: 20px;
    }
`;

// Default sort: pinned first, then wip, then others (keep original array order)
const sortDefault = (projectsList) => {
    return [...projectsList].sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        if (a.status === 'wip' && b.status !== 'wip') return -1;
        if (a.status !== 'wip' && b.status === 'wip') return 1;
        return 0;
    });
};

export const SORT_OPTIONS = [
    { value: 'default', labelKey: 'projects.sort.default' },
    { value: 'mostSponsored', labelKey: 'projects.sort.mostSponsored' },
    { value: 'highestValue', labelKey: 'projects.sort.highestValue' },
    { value: 'recentActivity', labelKey: 'projects.sort.recentActivity' },
];

const SORT_STORAGE_KEY = 'kafuka.projects.sortBy';

const applySort = (projects, sortBy, sponsorships) => {
    if (sortBy === 'default') return sortDefault(projects);
    const scored = projects.map((p) => {
        const s = sponsorships[p.id] || EMPTY_SPONSORSHIP;
        let score = 0;
        if (sortBy === 'mostSponsored') score = s.coffeeCount + s.mocktailCount;
        else if (sortBy === 'highestValue') score = s.coffeeTotalTWD + s.mocktailTotalTWD;
        else if (sortBy === 'recentActivity') score = s.lastSponsoredAt;
        return { project: p, score };
    });
    scored.sort((a, b) => b.score - a.score);
    return scored.map((x) => x.project);
};

const EMPTY_SPONSORSHIP = {
    coffeeCount: 0,
    mocktailCount: 0,
    coffeeTotalTWD: 0,
    mocktailTotalTWD: 0,
    lastSponsoredAt: 0,
};

const getSponsorship = (sponsorships, id) => sponsorships[id] || EMPTY_SPONSORSHIP;

const ProjectsPage = () => {
    const { t } = useTranslation();
    const [sponsorships, setSponsorships] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedSubProject, setSelectedSubProject] = useState(null);
    const [showDrinkModal, setShowDrinkModal] = useState({});
    const [toast, setToast] = useState(null);
    const [expandedProjects, setExpandedProjects] = useState({});

    // Handle post-Stripe-Checkout redirect (?sponsor=success&project=...&type=...)
    // Records the sponsorship client-side if the webhook hasn't already done so.
    // A localStorage guard prevents double-counting on manual refresh of the URL.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const params = new URLSearchParams(window.location.search);
        if (params.get('sponsor') !== 'success') return;
        const projectId = params.get('project');
        const type = params.get('type');
        if (!projectId || !type) return;

        const guardKey = `kafuka.sponsor.ack.${projectId}.${type}.${window.location.search}`;
        let alreadyAcked = false;
        try {
            alreadyAcked = Boolean(localStorage.getItem(guardKey));
        } catch {
            // ignore
        }

        const cleanupUrl = () => {
            const url = new URL(window.location.href);
            ['sponsor', 'project', 'type'].forEach((k) => url.searchParams.delete(k));
            window.history.replaceState({}, document.title, url.pathname + url.search);
        };

        if (alreadyAcked) {
            cleanupUrl();
            return;
        }

        (async () => {
            try {
                if (isFirebaseReady()) await recordSponsorship(projectId, type);
                try { localStorage.setItem(guardKey, '1'); } catch { /* ignore */ }
                const amount = SPONSOR_AMOUNT_TWD[type] || 0;
                setToast(
                    t('projects.sponsorSuccess', {
                        amount,
                        defaultValue: `感謝贊助 NT$${amount}！`,
                    })
                );
                setTimeout(() => setToast(null), 3500);
            } catch (err) {
                console.error('Failed to ack sponsorship:', err);
            } finally {
                cleanupUrl();
            }
        })();
    }, [t]);

    // Real-time sponsorship listener
    useEffect(() => {
        if (!isFirebaseReady()) {
            setLoading(false);
            return undefined;
        }
        try {
            const unsub = subscribeSponsorships((map) => {
                setSponsorships(map);
                setLoading(false);
            });
            return () => {
                if (typeof unsub === 'function') unsub();
            };
        } catch (err) {
            console.error('Error loading sponsorships:', err);
            setError(t('projects.loadVoteError'));
            setLoading(false);
            return undefined;
        }
    }, [t]);

    const [showStats, setShowStats] = useState(false);

    const [sortBy, setSortBy] = useState(() => {
        try {
            return localStorage.getItem(SORT_STORAGE_KEY) || 'default';
        } catch {
            return 'default';
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem(SORT_STORAGE_KEY, sortBy);
        } catch {
            // ignore quota/denied
        }
    }, [sortBy]);

    const projects = useMemo(
        () => applySort(projectsData, sortBy, sponsorships),
        [sortBy, sponsorships]
    );

    // Aggregate stats across all projects
    const stats = useMemo(() => {
        let totalCoffee = 0, totalMocktail = 0, totalTWD = 0, totalCount = 0;
        const byProject = projectsData.map((p) => {
            const s = sponsorships[p.id] || EMPTY_SPONSORSHIP;
            const count = s.coffeeCount + s.mocktailCount;
            const amount = s.coffeeTotalTWD + s.mocktailTotalTWD;
            totalCoffee += s.coffeeCount;
            totalMocktail += s.mocktailCount;
            totalTWD += amount;
            totalCount += count;
            return { id: p.id, title: p.title, count, amount, lastSponsoredAt: s.lastSponsoredAt };
        }).filter(p => p.count > 0).sort((a, b) => b.amount - a.amount);
        return { totalCoffee, totalMocktail, totalTWD, totalCount, byProject };
    }, [sponsorships]);

    const toggleProject = (projectId) => {
        setExpandedProjects(prev => ({
            ...prev,
            [projectId]: !prev[projectId]
        }));
    };

    const handleSupportClick = (projectId, e) => {
        e.stopPropagation();
        setShowDrinkModal(prev => ({
            ...prev,
            [projectId]: !prev[projectId]
        }));
    };

    // Close drink modal when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest('[data-support-button]') && !e.target.closest('[data-drink-modal]')) {
                setShowDrinkModal({});
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const handleVote = async (projectId, voteType) => {
        setShowDrinkModal(prev => ({ ...prev, [projectId]: false }));
        const amount = SPONSOR_AMOUNT_TWD[voteType] || 0;
        try {
            if (isFirebaseReady()) {
                const result = await startCheckout({ projectId, type: voteType });
                // Payment Link / Stripe session modes redirect the browser away;
                // only the mock path resolves here synchronously.
                if (result?.mode === 'mock') {
                    setToast(
                        t('projects.sponsorSuccess', {
                            amount,
                            defaultValue: `感謝贊助 NT$${amount}！（mock：尚未串 Stripe）`,
                        })
                    );
                }
            } else {
                // Firebase not configured — local only
                setSponsorships((prev) => {
                    const curr = prev[projectId] || EMPTY_SPONSORSHIP;
                    const countKey = voteType === 'coffee' ? 'coffeeCount' : 'mocktailCount';
                    const totalKey = voteType === 'coffee' ? 'coffeeTotalTWD' : 'mocktailTotalTWD';
                    return {
                        ...prev,
                        [projectId]: {
                            ...curr,
                            [countKey]: curr[countKey] + 1,
                            [totalKey]: curr[totalKey] + amount,
                            lastSponsoredAt: Date.now(),
                        },
                    };
                });
                setToast(t('projects.voteSuccessLocal'));
            }
            setTimeout(() => setToast(null), 3000);
        } catch (err) {
            console.error('Error sponsoring:', err);
            setError(t('projects.voteError'));
            setToast(t('projects.voteError'));
            setTimeout(() => setToast(null), 3000);
        }
    };

    const getStatusText = (status) => {
        const statusMap = {
            'done': t('projects.status.done'),
            'wip': t('projects.status.wip'),
            'pending': t('projects.status.pending'),
            'paused': t('projects.status.paused'),
            'not yet': t('projects.status.notYet')
        };
        return statusMap[status] || status;
    };

    const getDisplayTodos = (todos) => {
        if (!todos || todos.length === 0) return [];
        
        // 分离已完成和未完成的
        const pending = todos.filter(todo => !todo.completed);
        const completed = todos.filter(todo => todo.completed);
        
        // 未完成的优先，最多显示3个
        const displayTodos = [...pending, ...completed].slice(0, 3);
        
        return displayTodos;
    };

    if (loading) {
        return (
            <ProjectsContainer>
                <ContentWrapper>
                    <LoadingText>{t('projects.loading')}</LoadingText>
                </ContentWrapper>
            </ProjectsContainer>
        );
    }

    return (
        <ProjectsContainer>
            <ContentWrapper>
                <Header>
                    <Title>{t('projects.title')}</Title>
                    <Subtitle>{t('projects.subtitle')}</Subtitle>
                    <Description>
                        {t('projects.description')}
                        <br />
                        {t('projects.descriptionEn')}
                    </Description>
                </Header>

                {error && <ErrorText>{error}</ErrorText>}

                {isFirebaseReady() && (
                    <StatsPanel>
                        <StatsPanelHeader onClick={() => setShowStats(s => !s)}>
                            <StatsPanelTitle>
                                📊 {t('projects.stats.title', { defaultValue: '贊助統計' })}
                            </StatsPanelTitle>
                            <StatsPanelToggle>{showStats ? '▲ 收起' : '▼ 展開'}</StatsPanelToggle>
                        </StatsPanelHeader>
                        {showStats && (
                            <>
                                <StatsGrid>
                                    <StatCard>
                                        <StatValue color="#2ecc71">NT$ {stats.totalTWD.toLocaleString()}</StatValue>
                                        <StatLabel>{t('projects.stats.totalRevenue', { defaultValue: '累計贊助金額' })}</StatLabel>
                                    </StatCard>
                                    <StatCard>
                                        <StatValue>{stats.totalCount}</StatValue>
                                        <StatLabel>{t('projects.stats.totalCount', { defaultValue: '總贊助次數' })}</StatLabel>
                                    </StatCard>
                                    <StatCard>
                                        <StatValue color="#c8a97e">☕ {stats.totalCoffee}</StatValue>
                                        <StatLabel>{t('projects.stats.coffeeCount', { defaultValue: '咖啡' })}</StatLabel>
                                    </StatCard>
                                    <StatCard>
                                        <StatValue color="#ff6b9d">🍹 {stats.totalMocktail}</StatValue>
                                        <StatLabel>{t('projects.stats.mocktailCount', { defaultValue: '特調' })}</StatLabel>
                                    </StatCard>
                                </StatsGrid>
                                {stats.byProject.length > 0 && (
                                    <StatsProjectTable>
                                        {stats.byProject.map((p) => (
                                            <StatsProjectRow key={p.id}>
                                                <StatsProjectName>{p.title}</StatsProjectName>
                                                <StatsProjectNumbers>
                                                    <span>{p.count} 次</span>
                                                    <StatsProjectAmount>NT$ {p.amount.toLocaleString()}</StatsProjectAmount>
                                                </StatsProjectNumbers>
                                            </StatsProjectRow>
                                        ))}
                                    </StatsProjectTable>
                                )}
                                {stats.byProject.length === 0 && (
                                    <p style={{ textAlign: 'center', opacity: 0.5, fontSize: '0.85rem', marginTop: '16px' }}>
                                        {t('projects.stats.noData', { defaultValue: '尚無贊助記錄' })}
                                    </p>
                                )}
                            </>
                        )}
                    </StatsPanel>
                )}

                <SortBar>
                    <SortLabel>{t('projects.sort.label', { defaultValue: '排序' })}</SortLabel>
                    <SortSelect
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                    >
                        {SORT_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {t(opt.labelKey, {
                                    defaultValue:
                                        opt.value === 'default' ? '預設'
                                        : opt.value === 'mostSponsored' ? '最多贊助'
                                        : opt.value === 'highestValue' ? '贊助金額最高'
                                        : '最近活動',
                                })}
                            </option>
                        ))}
                    </SortSelect>
                </SortBar>

                {projects.map((project) => {
                    const isExpanded = expandedProjects[project.id];
                    return (
                        <ProjectCard key={project.id}>
                            <ProjectHeader 
                                expanded={isExpanded}
                                onClick={() => toggleProject(project.id)}
                            >
                                <ProjectHeaderCollapsed>
                                    <ProjectHeaderLeft>
                                        <ProjectTitle>
                                            {project.title}
                                            {project.url && (
                                                isInternalRoute(project.url) ? (
                                                    <Link to={project.url} onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center' }}>
                                                        <SvgLink />
                                                    </Link>
                                                ) : (
                                                    <SvgLink href={project.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} />
                                                )
                                            )}
                                        </ProjectTitle>
                                        {project.pinned && <PinnedBadge aria-label="pinned">📌</PinnedBadge>}
                                        <StatusBadge status={project.status}>
                                            {getStatusText(project.status)}
                                        </StatusBadge>
                                    </ProjectHeaderLeft>
                                    <ProjectHeaderRight>
                                        <VotingSectionWrapper expanded={isExpanded}>
                                            <VotingSection expanded={isExpanded} onClick={(e) => e.stopPropagation()}>
                                                {!isExpanded && (
                                                    <SupportButton
                                                        data-support-button
                                                        onClick={(e) => handleSupportClick(project.id, e)}
                                                    >
                                                        {t('projects.sponsorSupport')}
                                                    </SupportButton>
                                                )}
                                                {showDrinkModal[project.id] && !isExpanded && (
                                                    <DrinkModal data-drink-modal>
                                                    <DrinkOption
                                                        voteType="coffee"
                                                        onClick={() => handleVote(project.id, 'coffee')}
                                                    >
                                                        ☕ {t('projects.coffee')}
                                                    </DrinkOption>
                                                    <DrinkOption
                                                        voteType="mocktail"
                                                        onClick={() => handleVote(project.id, 'mocktail')}
                                                    >
                                                        🍹 {t('projects.mocktail')}
                                                    </DrinkOption>
                                                    </DrinkModal>
                                                )}
                                            </VotingSection>
                                        </VotingSectionWrapper>
                                        <ExpandButton onClick={(e) => { e.stopPropagation(); toggleProject(project.id); }}>
                                            {isExpanded ? '−' : '+'}
                                        </ExpandButton>
                                    </ProjectHeaderRight>
                                </ProjectHeaderCollapsed>
                            </ProjectHeader>

                            <ProjectContent expanded={isExpanded}>
                                {project.subtitle && (
                                    <ProjectDescription style={{ fontStyle: 'italic', opacity: 0.8, marginBottom: '10px' }}>
                                        {project.subtitle}
                                    </ProjectDescription>
                                )}

                                {project.thumbnail && (
                                    project.url ? (
                                        isInternalRoute(project.url) ? (
                                            <ProjectThumbnail 
                                                as={Link}
                                                to={project.url}
                                            >
                                                <img 
                                                    src={project.thumbnail} 
                                                    alt={`${project.title} 縮圖`}
                                                    loading="lazy"
                                                />
                                                <ThumbnailOverlay className="thumbnail-overlay">
                                                    <ThumbnailOverlayIcon viewBox="0 0 16 16">
                                                        <path d="M13.58 7.37334L8.44662 12.5067C7.64159 13.3279 6.56645 13.83 5.41995 13.92H5.12662C4.129 13.9315 3.16844 13.5424 2.45995 12.84C0.899951 11.28 1.05328 8.58667 2.79995 6.84L7.39995 2.24C7.65998 1.98152 8.07993 1.98152 8.33995 2.24C8.46616 2.36518 8.53715 2.53558 8.53715 2.71334C8.53715 2.8911 8.46616 3.06149 8.33995 3.18667L3.73328 7.79334C2.49995 9.02 2.34662 10.8667 3.38662 11.9067C3.9063 12.3992 4.61394 12.6424 5.32662 12.5733C6.15126 12.5048 6.92323 12.1401 7.49995 11.5467L12.6666 6.43334C13.4403 5.77611 13.5469 4.62109 12.9066 3.83334C12.121 3.19381 10.9682 3.30051 10.3133 4.07334L5.17995 9.20667C5.01212 9.36956 4.90807 9.58711 4.88662 9.82C4.86791 9.99041 4.92382 10.1606 5.03995 10.2867C5.37939 10.5316 5.85028 10.4735 6.11995 10.1533L11.5266 4.74667C11.7866 4.48819 12.2066 4.48819 12.4666 4.74667C12.5928 4.87185 12.6638 5.04225 12.6638 5.22C12.6638 5.39776 12.5928 5.56816 12.4666 5.69334L7.06662 11.0933C6.28439 11.9477 4.95916 12.0102 4.09995 11.2333C3.32135 10.3759 3.3809 9.05078 4.23328 8.26667L9.33328 3.13334C10.5101 1.83679 12.5115 1.72973 13.82 2.89334C14.9828 4.19954 14.8757 6.19885 13.58 7.37334Z" fill="currentColor"></path>
                                                    </ThumbnailOverlayIcon>
                                                    {t('projects.viewProject')}
                                                </ThumbnailOverlay>
                                            </ProjectThumbnail>
                                        ) : (
                                            <ProjectThumbnail 
                                                href={project.url} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                            >
                                                <img 
                                                    src={project.thumbnail} 
                                                    alt={`${project.title} 縮圖`}
                                                    loading="lazy"
                                                />
                                                <ThumbnailOverlay className="thumbnail-overlay">
                                                    <ThumbnailOverlayIcon viewBox="0 0 16 16">
                                                        <path d="M13.58 7.37334L8.44662 12.5067C7.64159 13.3279 6.56645 13.83 5.41995 13.92H5.12662C4.129 13.9315 3.16844 13.5424 2.45995 12.84C0.899951 11.28 1.05328 8.58667 2.79995 6.84L7.39995 2.24C7.65998 1.98152 8.07993 1.98152 8.33995 2.24C8.46616 2.36518 8.53715 2.53558 8.53715 2.71334C8.53715 2.8911 8.46616 3.06149 8.33995 3.18667L3.73328 7.79334C2.49995 9.02 2.34662 10.8667 3.38662 11.9067C3.9063 12.3992 4.61394 12.6424 5.32662 12.5733C6.15126 12.5048 6.92323 12.1401 7.49995 11.5467L12.6666 6.43334C13.4403 5.77611 13.5469 4.62109 12.9066 3.83334C12.121 3.19381 10.9682 3.30051 10.3133 4.07334L5.17995 9.20667C5.01212 9.36956 4.90807 9.58711 4.88662 9.82C4.86791 9.99041 4.92382 10.1606 5.03995 10.2867C5.37939 10.5316 5.85028 10.4735 6.11995 10.1533L11.5266 4.74667C11.7866 4.48819 12.2066 4.48819 12.4666 4.74667C12.5928 4.87185 12.6638 5.04225 12.6638 5.22C12.6638 5.39776 12.5928 5.56816 12.4666 5.69334L7.06662 11.0933C6.28439 11.9477 4.95916 12.0102 4.09995 11.2333C3.32135 10.3759 3.3809 9.05078 4.23328 8.26667L9.33328 3.13334C10.5101 1.83679 12.5115 1.72973 13.82 2.89334C14.9828 4.19954 14.8757 6.19885 13.58 7.37334Z" fill="currentColor"></path>
                                                    </ThumbnailOverlayIcon>
                                                    {t('projects.viewProject')}
                                                </ThumbnailOverlay>
                                            </ProjectThumbnail>
                                        )
                                    ) : (
                                        <ProjectThumbnail as="div">
                                            <img 
                                                src={project.thumbnail} 
                                                alt={`${project.title} 縮圖`}
                                                loading="lazy"
                                            />
                                        </ProjectThumbnail>
                                    )
                                )}

                                <ProjectDescription>{project.description}</ProjectDescription>

                                {(project.repo || project.originalRepo) && (
                                    <ProjectDescription style={{ fontSize: '0.85em', opacity: 0.75, marginTop: '-4px' }}>
                                        {project.repo && (
                                            <>
                                                Repo: <a href={project.repo} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{project.repo.replace('https://github.com/', '')}</a>
                                            </>
                                        )}
                                        {project.repo && project.originalRepo && ' · '}
                                        {project.originalRepo && (
                                            <>
                                                Forked from <a href={project.originalRepo} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{project.originalRepo.replace('https://github.com/', '')}</a>
                                            </>
                                        )}
                                    </ProjectDescription>
                                )}

                                {project.subProjects && project.subProjects.length > 0 && (
                                    <SubProjectsSection>
                                        <SubProjectsIndicator />
                                        {project.subProjects.map((subProject, index) => (
                                            <SubProjectItem 
                                                key={index}
                                                onClick={() => setSelectedSubProject({ ...subProject, parentProject: project.title })}
                                            >
                                                <SubProjectName>
                                                    {subProject.name}
                                                    <SubProjectStatus status={subProject.status}>
                                                        {getStatusText(subProject.status)}
                                                    </SubProjectStatus>
                                                </SubProjectName>
                                                {subProject.description && (
                                                    <SubProjectDescription>{subProject.description}</SubProjectDescription>
                                                )}
                                            </SubProjectItem>
                                        ))}
                                    </SubProjectsSection>
                                )}

                                {project.todos && project.todos.length > 0 && getDisplayTodos(project.todos).length > 0 && (
                                    <TodosSection>
                                        <TodosTitle>📋 {t('projects.todos')}</TodosTitle>
                                        <TodosList>
                                            {getDisplayTodos(project.todos).map((todo, index) => (
                                                <TodoItem key={index} completed={todo.completed}>
                                                    {todo.text}
                                                </TodoItem>
                                            ))}
                                        </TodosList>
                                    </TodosSection>
                                )}
                            </ProjectContent>

                            {isExpanded && (
                                <VotingSectionWrapper expanded={true}>
                                    <VotingSection expanded={true} onClick={(e) => e.stopPropagation()}>
                                            <SupportButton
                                                data-support-button
                                                onClick={(e) => handleSupportClick(project.id, e)}
                                            >
                                                贊助支持
                                            </SupportButton>
                                            <TotalVotes>
                                                {(() => {
                                                    const s = getSponsorship(sponsorships, project.id);
                                                    return `${s.coffeeCount + s.mocktailCount} ${t('projects.votes')}`;
                                                })()}
                                            </TotalVotes>
                                        {showDrinkModal[project.id] && (
                                            <DrinkModal data-drink-modal>
                                                    <DrinkOption
                                                        voteType="coffee"
                                                        onClick={() => handleVote(project.id, 'coffee')}
                                                    >
                                                        ☕ {t('projects.coffee')}
                                                    </DrinkOption>
                                                    <DrinkOption
                                                        voteType="mocktail"
                                                        onClick={() => handleVote(project.id, 'mocktail')}
                                                    >
                                                        🍹 {t('projects.mocktail')}
                                                    </DrinkOption>
                                            </DrinkModal>
                                        )}
                                    </VotingSection>
                                </VotingSectionWrapper>
                            )}
                        </ProjectCard>
                    );
                })}

                <InfoText>
                    💡 {t('projects.voteInfoPaypal')}
                    <br />
                    {!isFirebaseReady() ? (
                        <span style={{ color: '#f39c12' }}>
                            {t('projects.firebaseNotConfigured')}
                        </span>
                    ) : (
                        t('projects.firebaseConfigured')
                    )}
                </InfoText>
            </ContentWrapper>

            {selectedSubProject && (
                <ModalOverlay onClick={() => setSelectedSubProject(null)}>
                    <ModalContent onClick={(e) => e.stopPropagation()}>
                        <ModalHeader>
                            <ModalTitle>
                                {selectedSubProject.name}
                                {selectedSubProject.parentProject && (
                                    <ParentProjectLabel>
                                        ({selectedSubProject.parentProject})
                                    </ParentProjectLabel>
                                )}
                                <SubProjectStatus status={selectedSubProject.status}>
                                    {getStatusText(selectedSubProject.status)}
                                </SubProjectStatus>
                            </ModalTitle>
                            <CloseButton onClick={() => setSelectedSubProject(null)}>×</CloseButton>
                        </ModalHeader>

                        {selectedSubProject.description && (
                            <ModalDescription>{selectedSubProject.description}</ModalDescription>
                        )}

                        {selectedSubProject.todos && selectedSubProject.todos.length > 0 && selectedSubProject.name !== '音樂頁面' && (
                            <ChangelogSection>
                                <ChangelogTitle>📋 Changelog</ChangelogTitle>
                                <ChangelogList>
                                    {selectedSubProject.todos.map((todo, index) => (
                                        <ChangelogItem key={index} completed={todo.completed}>
                                            <ChangelogIcon>
                                                {todo.completed ? '✓' : '○'}
                                            </ChangelogIcon>
                                            <ChangelogText completed={todo.completed}>
                                                {todo.text}
                                            </ChangelogText>
                                        </ChangelogItem>
                                    ))}
                                </ChangelogList>
                            </ChangelogSection>
                        )}

                        {selectedSubProject.name === '音樂頁面' && (
                            <ViewButton to="/music" onClick={() => setSelectedSubProject(null)}>
                                🎵 前往音樂頁面 →
                            </ViewButton>
                        )}
                    </ModalContent>
                </ModalOverlay>
            )}

            {toast && (
                <Toast>
                    {toast}
                </Toast>
            )}
        </ProjectsContainer>
    );
};

export default ProjectsPage;


